import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, QrCode, X, XCircle } from "lucide-react";

const fmtIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n);

export const QrisModal = ({ payment, onClose, onPaid }) => {
  const [state, setState] = useState("pending");
  const timer = useRef(null);

  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/qris/status/${payment.order_id}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          onPaid?.();
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setState(r.data.payment_status);
          return;
        }
      } catch {
        // keep polling
      }
      if (!stopped) timer.current = setTimeout(poll, 4000);
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.order_id]);

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card max-w-md w-full p-8 relative text-center" onClick={(e) => e.stopPropagation()} data-testid="qris-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white" data-testid="qris-close">
          <X className="w-5 h-5" />
        </button>
        <div className="chip chip-cyan mb-4 mx-auto"><QrCode className="w-3 h-3" /> QRIS</div>
        <h2 className="font-heading text-2xl font-black">Scan untuk Bayar</h2>
        <div className="font-heading text-3xl font-black gradient-text mt-1" data-testid="qris-amount">{payment.amount ? fmtIDR(payment.amount) : "Memuat…"}</div>
        <p className="text-slate-500 text-sm mt-2">GoPay · OVO · DANA · ShopeePay · Mobile Banking (semua aplikasi QRIS)</p>

        {state === "pending" && (
          <>
            {payment.redirect_url ? (
              <div className="mt-6" data-testid="qris-redirect">
                <p className="text-slate-300 text-sm">Halaman pembayaran Midtrans telah dibuka di tab baru. Pilih QRIS / e-wallet / transfer bank, lalu kembali ke sini.</p>
                <a href={payment.redirect_url} target="_blank" rel="noreferrer" className="btn-primary mt-4 inline-flex" data-testid="qris-open-payment">
                  <QrCode className="w-4 h-4" /> Buka Halaman Pembayaran
                </a>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-3 inline-block mt-6">
                {payment.qr_url ? (
                  <img src={payment.qr_url} alt="QRIS" className="w-56 h-56" data-testid="qris-image" />
                ) : (
                  <div className="w-56 h-56 flex items-center justify-center text-slate-500 text-sm">Menunggu konfirmasi pembayaran…</div>
                )}
              </div>
            )}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mt-5" data-testid="qris-waiting">
              <Loader2 className="w-4 h-4 animate-spin text-cyan1" /> Menunggu pembayaran… (berlaku 30 menit)
            </div>
            <p className="text-[11px] font-mono text-slate-600 mt-2">Order: {payment.order_id}</p>
          </>
        )}
        {state === "paid" && (
          <div className="mt-8" data-testid="qris-paid">
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto" />
            <p className="font-heading text-xl font-bold mt-3">Pembayaran Berhasil!</p>
            <p className="text-slate-400 text-sm">Langganan Anda sudah aktif. Selamat menikmati digest premium.</p>
            <button onClick={onClose} className="btn-primary mt-6" data-testid="qris-done">Lanjutkan</button>
          </div>
        )}
        {(state === "failed" || state === "expired") && (
          <div className="mt-8" data-testid="qris-failed">
            <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            <p className="font-heading text-xl font-bold mt-3">{state === "expired" ? "QR Kedaluwarsa" : "Pembayaran Gagal"}</p>
            <button onClick={onClose} className="btn-ghost mt-6">Tutup</button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export { fmtIDR };
